import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Build as FaultsIcon,
  SwapHoriz as AlternativesIcon,
  Image as DatasheetIcon,
  Article as ArticleIcon,
  Category as CategoryIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import FaultsPage from './maintenanceBank/FaultsPage';
import CoilAlternativesPage from './maintenanceBank/CoilAlternativesPage';
import CoilDatasheetPage from './maintenanceBank/CoilDatasheetPage';
import { FaultCategoryService, FaultTopicService } from '../services/maintenanceBankService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-bank-tabpanel-${index}`}
      aria-labelledby={`maintenance-bank-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `maintenance-bank-tab-${index}`,
    'aria-controls': `maintenance-bank-tabpanel-${index}`,
  };
}

const MaintenanceBankPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState({
    totalCategories: 0,
    totalTopics: 0,
    totalViews: 0,
    publishedTopics: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // تحميل الإحصائيات
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      
      // تحميل الفئات
      const categories = await FaultCategoryService.getCategories();
      
      // تحميل جميع المواضيع من كل الفئات
      let allTopics: any[] = [];
      let totalViews = 0;
      let publishedCount = 0;

      for (const category of categories) {
        try {
          const result = await FaultTopicService.getAllTopicsByCategory(category.id);
          allTopics = [...allTopics, ...result.items];
          
          // حساب المشاهدات والمواضيع المنشورة
          result.items.forEach((topic: any) => {
            totalViews += topic.views || 0;
            if (topic.isPublished) publishedCount++;
          });
        } catch (error) {
          console.error(`خطأ في تحميل مواضيع الفئة ${category.id}:`, error);
        }
      }

      setStats({
        totalCategories: categories.length,
        totalTopics: allTopics.length,
        totalViews,
        publishedTopics: publishedCount
      });
    } catch (error) {
      console.error('خطأ في تحميل الإحصائيات:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* العنوان الرئيسي */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            🏦 بنك الصيانة
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            مركز المعرفة الشامل للأعطال وبدائل الكوفات والمساطر
          </Typography>
        </Paper>

        {/* بطاقات الإحصائيات */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {loadingStats ? <CircularProgress size={24} sx={{ color: 'white' }} /> : stats.totalCategories}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      فئة
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {loadingStats ? <CircularProgress size={24} sx={{ color: 'white' }} /> : stats.totalTopics}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      موضوع
                    </Typography>
                  </Box>
                  <ArticleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {loadingStats ? <CircularProgress size={24} sx={{ color: 'white' }} /> : stats.publishedTopics}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      منشور
                    </Typography>
                  </Box>
                  <TrendingIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {loadingStats ? <CircularProgress size={24} sx={{ color: 'white' }} /> : stats.totalViews}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      مشاهدة
                    </Typography>
                  </Box>
                  <ViewIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* التبويبات */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="maintenance bank tabs"
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 72,
                fontSize: '1.1rem',
                fontWeight: 600,
                transition: 'all 0.3s ease'
              }
            }}
          >
            <Tab
              icon={<FaultsIcon sx={{ fontSize: 28 }} />}
              label="الأعطال والحلول"
              {...a11yProps(0)}
              sx={{
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}
            />
            <Tab
              icon={<AlternativesIcon sx={{ fontSize: 28 }} />}
              label="بدائل الكوفات والمساطر"
              {...a11yProps(1)}
              sx={{
                '&.Mui-selected': {
                  color: 'secondary.main',
                  backgroundColor: 'secondary.50'
                }
              }}
            />
            <Tab
              icon={<DatasheetIcon sx={{ fontSize: 28 }} />}
              label="داتا شيت الكوفات"
              {...a11yProps(2)}
              sx={{
                '&.Mui-selected': {
                  color: 'success.main',
                  backgroundColor: 'success.50'
                }
              }}
            />
          </Tabs>
        </Paper>

        {/* محتوى التبويبات */}
        <TabPanel value={currentTab} index={0}>
          <FaultsPage />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <CoilAlternativesPage />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <CoilDatasheetPage />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default MaintenanceBankPage;
